import type { SelectOptions } from '../Select/types';
import type { CompoundInputProps, CompoundInputValue } from './types';

import { flattenOptions } from '../Select/utils';

/**
 * Inline width below which the segments stack. Two segments at the 40px-tall
 * field's minimum comfortable width (`--eds--size-120` each) stop being usable
 * side by side under this.
 */
export const STACK_THRESHOLD_PX = 240;

/**
 * Reorders the segments for the stacked layout. The order is applied to the
 * DOM rather than through CSS `order`, so focus order keeps matching what is
 * on screen.
 */
export function resolveSegmentOrder<V extends CompoundInputValue>(
  segments: CompoundInputProps<V>['segments'],
  stackOrder: CompoundInputProps<V>['stackOrder'],
  isStacked: boolean,
) {
  if (!isStacked || !stackOrder) {
    return segments;
  }

  const ordered = stackOrder
    .map((name) => segments.find((segment) => segment.name === name))
    .filter((segment) => segment !== undefined);

  return ordered.length === segments.length ? ordered : segments;
}

/**
 * Characters the widest thing a `select` segment can show, so a "hug" segment
 * can be sized in `ch` without measuring text. Sized on the widest option
 * rather than the selected one: a half that resized with every selection would
 * shift the other half around under the pointer.
 */
export function resolveHugCharacters<V>(
  options: SelectOptions<V>,
  placeholder?: string,
) {
  return flattenOptions(options).reduce(
    (widest, option) => Math.max(widest, option.text.length),
    placeholder?.length ?? 0,
  );
}

/**
 * Replaces one segment's entry in the composite value. Keyed writes widen the
 * result past `V`, so the cast is kept here rather than at each call site.
 */
export function withSegmentValue<V extends CompoundInputValue>(
  value: V,
  name: string,
  next: unknown,
) {
  return { ...value, [name]: next } as V;
}
